/**
 * useDashboardFeed — subscribes to the unified server feed of system
 * events for the dashboard terminal.
 *
 * On mount:
 *   1. Fetches /api/luna/dashboard/feed?limit=60 for an immediate
 *      snapshot of recent history (so the modal can scroll right
 *      away).
 *   2. Opens an EventSource on /api/luna/dashboard/stream for live
 *      updates. The first SSE event is `snapshot` (history burst),
 *      subsequent events are `line` (new entries).
 *
 * Returns the current array of events, newest first, capped at 60.
 *
 * All connected clients receive the SAME events at the same time
 * because the backend uses a single shared ring buffer.
 */
import { useEffect, useRef, useState } from 'react';

export type FeedLevel = 'OK' | 'INFO' | 'DBG' | 'WARN' | 'ERR';

export interface FeedEntry {
  id: number;
  ts: number;
  level: FeedLevel;
  text: string;
}

const HISTORY_LIMIT = 60;
const FEED_URL = '/api/luna/dashboard/feed?limit=60';
const STREAM_URL = '/api/luna/dashboard/stream';

function parseSnapshot(data: string): FeedEntry[] | null {
  try {
    const parsed = JSON.parse(data);
    if (Array.isArray(parsed)) return parsed as FeedEntry[];
  } catch {
    /* ignore */
  }
  return null;
}

function parseLine(data: string): FeedEntry | null {
  try {
    const parsed = JSON.parse(data);
    if (parsed && typeof parsed.id === 'number') return parsed as FeedEntry;
  } catch {
    /* ignore */
  }
  return null;
}

export function useDashboardFeed(): FeedEntry[] {
  const [lines, setLines] = useState<FeedEntry[]>([]);
  const seenIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;

    // 1. Initial REST snapshot for immediate scrollable history
    fetch(FEED_URL, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data || !data.success) return;
        const events: FeedEntry[] = data.events || [];
        seenIds.current = new Set(events.map((e) => e.id));
        setLines(events);
      })
      .catch(() => {
        /* network failure — fall through to EventSource */
      });

    // 2. SSE stream for live updates
    try {
      es = new EventSource(STREAM_URL);

      es.addEventListener('snapshot', (e: MessageEvent) => {
        if (cancelled) return;
        const events = parseSnapshot(e.data);
        if (events) {
          seenIds.current = new Set(events.map((ev) => ev.id));
          setLines(events);
        }
      });

      es.addEventListener('line', (e: MessageEvent) => {
        if (cancelled) return;
        const entry = parseLine(e.data);
        if (!entry) return;
        if (seenIds.current.has(entry.id)) return;
        seenIds.current.add(entry.id);
        setLines((prev) => {
          const next = [entry, ...prev];
          // Trim to history limit and keep ids in sync
          if (next.length > HISTORY_LIMIT) {
            const removed = next.slice(HISTORY_LIMIT);
            for (const r of removed) seenIds.current.delete(r.id);
            return next.slice(0, HISTORY_LIMIT);
          }
          return next;
        });
      });

      es.addEventListener('error', () => {
        // EventSource auto-reconnects, no action needed
      });
    } catch {
      /* SSE not supported — REST snapshot was the fallback */
    }

    return () => {
      cancelled = true;
      if (es) es.close();
    };
  }, []);

  return lines;
}
